import type {
	IEvidenceCheckResult,
	IEvidenceOverall,
	ITransferDocument,
	ITransferTarget,
	IVaultDocument,
} from './types';

import { useCallback } from 'react';

import { DateTime } from 'luxon';
import { useRecoilValue } from 'recoil';

import { loginState } from 'global-stores';

/*
 * Pure helpers, ported 1:1 from `esign.frontend/src/views/evault/store/helpers.ts`.
 *
 * They are plain TypeScript with no Recoil and no render dependency, and two of
 * them carry behaviour a rewrite would quietly change: `eligibleTransferTargets`
 * is an INTERSECTION, not a union, and `previewLineWidths` is a seeded LCG whose
 * exact arithmetic is what keeps a card from reshuffling between renders. Copied
 * rather than re-derived for that reason.
 */

/**
 * "2 min ago", "3 days ago". Falls back to an empty string on an unparseable
 * timestamp rather than rendering "Invalid DateTime" into the vault header.
 */
export const toRelativeTime = (iso: string): string => {
	const parsed = DateTime.fromISO(iso);
	if (!parsed.isValid) return '';
	return parsed.toRelative({ style: 'short' }) ?? '';
};

const KB = 1024;
const UNITS = ['B', 'KB', 'MB', 'GB'];

export const formatFileSize = (bytes: number): string => {
	if (!bytes || bytes < 0) return '—';
	const exponent = Math.min(
		Math.floor(Math.log(bytes) / Math.log(KB)),
		UNITS.length - 1
	);
	const value = bytes / KB ** exponent;
	const unit = UNITS[exponent] ?? 'B';
	return `${value.toFixed(exponent === 0 ? 0 : 1)} ${unit}`;
};

/**
 * Two decimals, always — `$4.99 / month`, `$0.99 per transferred document`.
 *
 * The same rule `billing/deck/derive.ts` applies, restated rather than imported:
 * that module's other exports drag the whole billing type surface in for one
 * `toFixed`, and a price shown to one decimal ("$4.9") reads as a typo.
 * Amounts always come from `GET /evault-addons` (D7) — never a literal here.
 */
export const money = (n: number | null | undefined) =>
	n === null || n === undefined
		? '—'
		: n.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  });

/** Document ids render with a leading hash: `DOC-2026-0041` → `#DOC-2026-0041`. */
export const formatDisplayId = (displayId: string): string =>
	displayId.startsWith('#') ? displayId : `#${displayId}`;

/**
 * The card preview is a stylised stand-in, not a page render, so it needs stable
 * pseudo-random line widths per document — deriving them from the id keeps a card
 * from reshuffling on every re-render.
 */
export const previewLineWidths = (seed: string, lines = 5): number[] => {
	let hash = 0;
	for (let index = 0; index < seed.length; index += 1) {
		hash = (hash * 31 + seed.charCodeAt(index)) % 100000;
	}
	return Array.from({ length: lines }, (_, index) => {
		hash = (hash * 1103515245 + 12345) % 2147483648;
		const spread = 45 + (hash % 50);
		return index === lines - 1 ? Math.max(30, spread - 20) : spread;
	});
};

/**
 * Recipients an original may legally be transferred to (D16).
 *
 * Only signers of the agreement are eligible, so the list is derived from the
 * documents themselves rather than a workspace directory. For a multi-document
 * selection this is the **intersection**: someone who signed only two of three
 * selected documents cannot receive all three, and the server would refuse the
 * whole transfer. Computing the intersection here means the picker never offers a
 * choice that is going to be rejected.
 *
 * Matching is by lower-cased email — `signers[]._id` is an envelope sub-document
 * id, not a user id, so it cannot be used to identify a person.
 */
export const eligibleTransferTargets = (
	documents: IVaultDocument[]
): ITransferTarget[] => {
	if (!documents.length) return [];

	const [first, ...rest] = documents;
	if (!first) return [];

	const seed = new Map<string, ITransferTarget>();
	(first.signers ?? []).forEach(signer => {
		const email = String(signer?.email ?? '')
			.toLowerCase()
			.trim();
		if (email) seed.set(email, { name: signer.name ?? email, email });
	});

	rest.forEach(doc => {
		const emails = new Set(
			(doc.signers ?? [])
				.map(s =>
					String(s?.email ?? '')
						.toLowerCase()
						.trim()
				)
				.filter(Boolean)
		);
		Array.from(seed.keys()).forEach(email => {
			if (!emails.has(email)) seed.delete(email);
		});
	});

	return Array.from(seed.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/* -------------------------------------------------------------------------- */
/*                              Audit event labels                            */
/* -------------------------------------------------------------------------- */

/**
 * The server stores machine keys; a person reads sentences. Unknown keys fall
 * through to the raw key rather than to a generic "updated" — the whole point of
 * distinct events (`vault_renamed` vs `vault_archived`) is that the trail reads
 * meaningfully, and a lossy fallback would undo that for any event added
 * upstream before this map catches up.
 */
export const AUDIT_EVENT_LABEL: Record<string, string> = {
	vault_created: 'Vault created',
	vault_renamed: 'Vault renamed',
	vault_archived: 'Vault archived',
	document_deposited: 'Document deposited',
	document_viewed: 'Document viewed',
	copy_downloaded: 'Copy downloaded',
	original_retrieved: 'Original retrieved',
	share_granted: 'Share granted',
	share_revoked: 'Share revoked',
	transfer_initiated: 'Transfer initiated',
	step_up_succeeded: 'Step-up succeeded',
	step_up_failed: 'Step-up failed',
	transfer_committed: 'Transfer committed',
	owner_notified: 'New owner notified',
	certificate_issued: 'Certificate of custody issued',
	seal_recomputed: 'Seal recomputed',
	reversal_requested: 'Reversal requested',
	production_exported: 'Production package exported',
	/* SIM-23, appended at the end so the server's enum and this map grow the same way. */
	checkout_initiated: 'Checkout started',
	document_checked_out: 'Checked out of the vault',
	document_revaulted: 'Re-vaulted',
};

export const auditEventLabel = (event: string): string =>
	AUDIT_EVENT_LABEL[event] ?? event;

/* -------------------------------------------------------------------------- */
/*                                  "You"                                     */
/* -------------------------------------------------------------------------- */

/**
 * A hook rather than a pure function because the answer depends on who is
 * logged in, and `loginState.userId` is where routes.tsx writes that after the
 * session resolves. Owner columns read far better as "You" than as a repeated
 * copy of the reader's own name, and the email still goes in `title` so the
 * substitution is never a loss of information.
 */
export const useIsYou = () => {
	const { userId } = useRecoilValue(loginState) ?? {};

	return useCallback(
		(ownerId?: string | null): boolean =>
			!!ownerId && !!userId && String(ownerId) === String(userId),
		[userId]
	);
};

/**
 * The session user's own email, lower-cased, or '' before the session resolves.
 *
 * A companion to `useIsYou` for the one place an id will not do: transfer
 * targets are matched by EMAIL, because `signers[]._id` is an envelope
 * sub-document id and not a user id. Kept beside it so both answers to "is this
 * me" come from the same place.
 */
export const useYourEmail = (): string => {
	const { email } = useRecoilValue(loginState) ?? {};
	return String(email ?? '').toLowerCase();
};

/* -------------------------------------------------------------------------- */
/*                                   Dates                                    */
/* -------------------------------------------------------------------------- */

/*
 * Deliberately the same two functions the eSign decks use
 * (`views/esign-deck/derive.ts`), copied rather than imported: a deposited-at
 * cell must read exactly like a sent-at cell, and importing across decks would
 * drag the eSign envelope types in for two date formatters. `toRelativeTime`
 * above is for the "updated 2 min ago" line; these are for a timestamp column.
 */
const pad = (n: number) => String(n).padStart(2, '0');
const MONTHS = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

export const dayOf = (v?: string | Date | null) => {
	if (!v) return '--';
	const d = new Date(v);
	if (Number.isNaN(d.getTime())) return '--';
	return `${MONTHS[d.getMonth()]} ${pad(d.getDate())}, ${d.getFullYear()}`;
};

export const timeOf = (v?: string | Date | null) => {
	if (!v) return '';
	const d = new Date(v);
	if (Number.isNaN(d.getTime())) return '';
	const h = d.getHours();
	return `${pad(h % 12 === 0 ? 12 : h % 12)}:${pad(d.getMinutes())} ${
		h < 12 ? 'AM' : 'PM'
	}`;
};

/** ISO, for the `title` of any cell that shows a formatted or relative time. */
export const isoOf = (v?: string | Date | null) => {
	if (!v) return undefined;
	const d = new Date(v);
	return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

/* -------------------------------------------------------------------------- */
/*                            Integrity presentation                          */
/* -------------------------------------------------------------------------- */

/**
 * R9's wording rules, in one place, as data.
 *
 * The mapping is the product decision — a check that could not be RUN is
 * neither a pass nor a failure, so `NOT_AVAILABLE` gets the neutral `ns` chip
 * and keeps the server's sentence beside it. Written as a table rather than a
 * ternary in the drawer so the three outcomes are visibly three, and so the day
 * a fourth arrives it is a row here instead of an `else`.
 *
 * The label is spelled out ("NOT AVAILABLE", not `NOT_AVAILABLE`): the enum
 * value is a machine key and the chip is read by a person.
 */
export const CHECK_CHIP: Record<
	IEvidenceCheckResult,
	{ cls: 'ok' | 'rej' | 'ns' | 'rev'; label: string }
> = {
	PASS: { cls: 'ok', label: 'PASS' },
	FAIL: { cls: 'rej', label: 'FAIL' },
	NOT_AVAILABLE: { cls: 'ns', label: 'NOT AVAILABLE' },
	/* Amber, like PARTIAL: something for a person to decide, nothing broken. */
	ELIGIBLE_FOR_DISPOSITION: { cls: 'rev', label: 'ELIGIBLE FOR DISPOSITION' },
};

/**
 * The overall verdict. `PARTIAL` is amber, not green: the report must not imply
 * a completeness it does not have, and amber is the only honest colour for "as
 * far as we can demonstrate". Unknown values fall back to the neutral chip
 * rather than to a pass.
 */
export const OVERALL_CHIP: Record<
	IEvidenceOverall,
	{ cls: 'ok' | 'rev' | 'rej'; label: string }
> = {
	VERIFIED: { cls: 'ok', label: 'VERIFIED' },
	PARTIAL: { cls: 'rev', label: 'PARTIAL' },
	FAILED: { cls: 'rej', label: 'FAILED' },
};

/**
 * A SHA-256 digest is 64 hex characters and a drawer is 480px wide.
 *
 * Truncated for reading, never for copying: the `CopyRow` beside it writes the
 * full value, which is the one that can be checked against
 * `shasum -a 256`. Returns the placeholder for a hash the server could not
 * produce, so an absent digest never reads as an empty box.
 */
export const shortHash = (hash: string | null | undefined, keep = 16): string => {
	if (!hash) return '—';
	return hash.length > keep ? `${hash.slice(0, keep)}…` : hash;
};

/**
 * The ledger chip a document row shows beside "Retained copy" (phase 1 Task 6).
 *
 * STALE IS CHECKED FIRST, not last. `sealHashAtRegistration` is only ever set
 * on branches that also set `ledger.documentId` (`evault-registry-hooks.ts`'s
 * `registryWriteFor` — the `exists` adoption is the other way round: it sets a
 * documentId and leaves the seal null), so stale is a subset of anchored and a
 * stale anchor is always ALSO an anchored one. Checking
 * "Anchored" first would shadow the one state that needs a person's attention
 * with the plain, nothing-to-see chip.
 */
export const registryChip = (
	doc: Pick<IVaultDocument, 'registry' | 'sealHash'>
): { verdict: 'ok' | 'wait' | 'rev'; label: string; title: string } | null => {
	const registry = doc.registry;
	if (!registry) return null;
	if (
		registry.sealHashAtRegistration &&
		registry.sealHashAtRegistration !== doc.sealHash
	) {
		return {
			verdict: 'rev',
			label: 'Anchor stale',
			title: 'This document was resealed after it was registered — the ledger entry records the earlier seal.',
		};
	}
	if (registry.ledger?.documentId) {
		return {
			verdict: 'ok',
			label: 'Anchored',
			title: 'This original is registered on the ledger.',
		};
	}
	if (registry.job?.status === 'queued' || registry.job?.status === 'running') {
		return {
			verdict: 'wait',
			label: 'Anchor pending',
			title: 'Registration on the ledger is queued and has not completed yet.',
		};
	}
	return null;
};

/* ── eVAULT TRANSFERS ──────────────────────────────────────────────────────
   Formatting for the Transfers page's period picker and Documents column.
   Moved here from `billing/deck/derive.ts` with the list itself. */

/** A row's documents, reduced to what the Documents column and its
 *  click-to-expand both need — ONE derivation, returning the documents
 *  themselves rather than a parallel list of names: the caller gates on
 *  `items.length` and renders `items`, so the thing counted is always the thing
 *  drawn. Never derived from `quantity` — that is the billed count and can
 *  exist with `documents` empty or absent. */
export const fmtDocs = (
	docs?: ITransferDocument[] | null
): { count: number; items: ITransferDocument[] } => {
	const items = (docs ?? []).filter(d => !!d?.name);
	return { count: items.length, items };
};

/** `YYYY-MM` for the current month, in UTC — the same clock the server's own
 *  `periodOf` uses, so the option labelled "(current)" always names the month
 *  `period=current` actually bills. */
export const currentPeriod = (): string => {
	const d = new Date();
	return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/** "September 2026" from a `YYYY-MM` period string. Falls back to the raw
 *  string rather than throwing on something unparsed — a picker option is not
 *  worth a blank screen. */
export const periodLabel = (period: string): string => {
	const m = /^(\d{4})-(\d{2})$/.exec(period);
	if (!m) return period;
	const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
	return d.toLocaleDateString(undefined, {
		month: 'long',
		year: 'numeric',
		timeZone: 'UTC',
	});
};

/**
 * May the person looking at this record act as its OWNER — transfer it, download the
 * original, export a production package, re-issue its certificate, reclassify it, or
 * take a disposition decision?
 *
 * Answered from `isRetainedCopy`, which the server computes as
 * `ownerId !== viewerId || externalOwner` — a direct comparison against the session
 * principal. It needs no `users` row, which is the whole point: `doc.owner` comes from a
 * $lookup that misses whenever a person's platform id differs from their esign user id,
 * and a miss is indistinguishable from "this record has no owner". On dev2 that hid the
 * owner's own controls on 18 of 40 records.
 *
 * STRICT `=== false`. The field is optional, and a missing one must read as "not yours":
 * `!doc.isRetainedCopy` would open every control by default on any path that omits it.
 */
export const canActAsOwner = (doc?: {
	isRetainedCopy?: boolean;
} | null): boolean => doc?.isRetainedCopy === false;
